<template>
	<div class="kanban-column" :class="{ 'kanban-column--backlog': isBacklog }">
		<!-- Header -->
		<div class="kanban-column__header" :style="headerStyle">
			<div class="d-flex align-center" style="gap:8px;min-width:0;">
				<v-btn v-if="hasColumnPermission && !isBacklog" icon x-small dark class="col-drag-handle" style="cursor:grab;flex-shrink:0;">
					<v-icon x-small>mdi-drag</v-icon>
				</v-btn>
				<div v-if="!isBacklog" class="column-color-dot" :style="{ backgroundColor: column.color || '#5e85ef' }"></div>
				<v-icon v-else small color="white">mdi-inbox-outline</v-icon>
				<span class="column-title">{{ column.name }}</span>
				<span class="column-counter">{{ tasks.length }}</span>
			</div>
			<div v-if="hasColumnPermission && !isBacklog" class="d-flex align-center" style="gap:2px;">
				<v-btn icon x-small dark @click="$emit('edit-column', column)">
					<v-icon x-small>mdi-pencil-outline</v-icon>
				</v-btn>
				<v-btn icon x-small dark @click="$emit('delete-column', column)">
					<v-icon x-small>mdi-delete-outline</v-icon>
				</v-btn>
			</div>
		</div>

		<!-- Buscador del Backlog -->
		<div v-if="isBacklog" class="backlog-search">
			<v-text-field
				v-model="localSearch"
				placeholder="Buscar tarea..."
				dense
				hide-details
				outlined
				clearable
				prepend-inner-icon="mdi-magnify"
				style="font-size:12px;"
				@input="$emit('search-change', localSearch || '')"
				@click:clear="$emit('search-change', '')"
			/>
		</div>

		<!-- Drop zone con vuedraggable -->
		<draggable
			class="kanban-column__body"
			:list="tasks"
			:group="dragGroup"
			:animation="200"
			ghost-class="kanban-ghost"
			chosen-class="kanban-chosen"
			drag-class="kanban-dragging"
			:disabled="false"
			:scroll="scrollContainer || true"
			:scroll-sensitivity="120"
			:scroll-speed="20"
			@start="onDragStart"
			@end="onDragEnd"
		>
			<KanbanCard
				v-for="item in tasks"
				:key="item._backlog ? 'backlog-' + item.task_id : item.id"
				:item="item"
				:isDraggable="canMoveTask(item)"
				:isBacklog="isBacklog"
				@open-task="$emit('open-task', $event)"
			/>

			<!-- Empty state -->
			<div v-if="tasks.length === 0" class="column-empty">
				<v-icon color="grey lighten-2" size="32">mdi-inbox-outline</v-icon>
				<p class="caption grey--text text--lighten-1 mb-0 mt-1 text-center">
					{{ isBacklog && localSearch ? 'Sin resultados para la búsqueda' : isBacklog ? 'Todas tus tareas están asignadas' : 'Sin tareas en esta columna' }}
				</p>
			</div>
		</draggable>
	</div>
</template>

<script>
import draggable from 'vuedraggable'
import KanbanCard from './KanbanCard.vue'

export default {
	name: 'KanbanColumn',
	components: { draggable, KanbanCard },
	props: {
		column: {
			type: Object,
			required: true
		},
		tasks: {
			type: Array,
			default: () => []
		},
		isBacklog: {
			type: Boolean,
			default: false
		},
		hasColumnPermission: {
			type: Boolean,
			default: false
		},
		currentUserId: {
			type: Number,
			default: null
		},
		scrollContainer: {
			default: null
		}
	},
	emits: ['drag-start', 'drag-end', 'edit-column', 'delete-column', 'search-change', 'open-task'],
	data: () => ({
		localSearch: ''
	}),
	computed: {
		dragGroup() {
			if (this.isBacklog) {
				return { name: 'kanban-tasks', pull: true, put: false }
			}
			return { name: 'kanban-tasks', pull: true, put: true }
		},
		headerStyle() {
			if (this.isBacklog) {
				return { backgroundColor: '#607D8B' }
			}
			return { backgroundColor: this.column.color || '#5e85ef' }
		}
	},
	methods: {
		canMoveTask(item) {
			if (!this.currentUserId) return false
			return item.task.created_by === this.currentUserId ||
				item.task.responsable === this.currentUserId || this.checkPermissions(['full_change_kanban'])
		},
		onDragStart(evt) {
			this.$emit('drag-start', evt)
		},
		onDragEnd(evt) {
			this.$emit('drag-end', { evt, column: this.column, isBacklog: this.isBacklog })
		}
	}
}
</script>

<style scoped>
.kanban-column {
	display: flex;
	flex-direction: column;
	min-width: 280px;
	max-width: 280px;
	background-color: #f5f6fa;
	border-radius: 8px;
	overflow: hidden;
	flex-shrink: 0;
}
.col-drag-handle {
	cursor: grab !important;
	opacity: 0.7;
}
.col-drag-handle:active {
	cursor: grabbing !important;
}
.kanban-column--backlog {
	min-width: 280px;
	max-width: 280px;
	background-color: #eceff1;
}
.kanban-column__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 12px;
	border-radius: 8px 8px 0 0;
	min-height: 44px;
}
.column-color-dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex-shrink: 0;
	border: 2px solid rgba(255,255,255,0.6);
}
.column-title {
	color: white;
	font-weight: 700;
	font-size: 13px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 160px;
}
.column-counter {
	background-color: rgba(255,255,255,0.3);
	color: white;
	border-radius: 10px;
	padding: 1px 7px;
	font-size: 11px;
	font-weight: 700;
	flex-shrink: 0;
}
.backlog-search {
	padding: 8px;
	background-color: #eceff1;
}
/deep/ .backlog-search .v-input__slot {
	background-color: white !important;
	min-height: 32px !important;
}
/deep/ .backlog-search .v-input__control {
	min-height: 32px !important;
}
/deep/ .backlog-search input {
	padding: 4px 0 !important;
	font-size: 12px;
}
.kanban-column__body {
	padding: 8px;
	flex: 1;
	min-height: 120px;
	overflow-y: auto;
	max-height: calc(100vh - 300px);
}
.column-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 24px 8px;
	border: 2px dashed #e0e0e0;
	border-radius: 6px;
	margin: 4px 0;
}
/* Estilos de drag & drop */
/deep/ .kanban-ghost {
	opacity: 0.4;
	background: #c8ebfb;
	border-radius: 4px;
}
/deep/ .kanban-chosen {
	box-shadow: 0 6px 20px rgba(0,0,0,0.15) !important;
}
/deep/ .kanban-dragging {
	transform: rotate(2deg);
}
</style>
